/**
 * Spectral Analyzer Visualizer - Shows 4 spectral features as horizontal gauges
 *
 * Displays: Brightness (centroid), Energy Rolloff, Spectral Flux, and Flatness
 * Useful for understanding spectral characteristics of audio.
 */

import { Base2DVisualizer } from '../lib/base-2d-visualizer.js';
import { VisualizerRegistry } from '../visualizer.js';

// Metadata for manifest generation
export const metadata = {
  name: 'spectral-analyzer',
  displayName: 'Spectral Analyzer',
  contextType: '2d',
};

class SpectralAnalyzer extends Base2DVisualizer {
  static name = 'spectral-analyzer';
  static displayName = 'Spectral Analyzer';

  /**
   * Get configuration UI definition
   */
  static getConfigUI() {
    return [
      { type: 'range', id: 'smoothing', label: 'Smoothing', min: 0, max: 95, default: 70, scale: 0.01 },
      { type: 'checkbox', id: 'showLabels', label: 'Show Labels', default: true },
      { type: 'checkbox', id: 'showValues', label: 'Show Values', default: true },
      { type: 'checkbox', id: 'glow', label: 'Glow Effect', default: true },
      { type: 'color', id: 'accentColor', label: 'Accent Color', default: '#00ffaa' },
      { type: 'color', id: 'backgroundColor', label: 'Background', default: '#0a0a0a' }
    ];
  }

  /**
   * Visualizer-specific default configuration
   */
  getDefaultConfig() {
    return {
      showLabels: true,
      showValues: true,
    };
  }

  /**
   * Initialize state
   */
  init() {
    this.featureNames = ['centroid', 'rolloff', 'flux', 'flatness'];
    this.featureLabels = ['Brightness', 'Energy Rolloff', 'Spectral Flux', 'Flatness (Tonal/Noise)'];
    this.smoothedValues = new Float32Array(4);
    this.peakValues = new Float32Array(4);
    this.peakDecay = 0.995;
  }

  /**
   * Returns the number of elements (4 gauges)
   */
  getElementCount() {
    return 4;
  }

  /**
   * Draw a horizontal gauge
   */
  drawGauge(label, value, index, maxY) {
    const { ctx, canvas, config } = this;
    const { showLabels, showValues, accentColor } = config;

    const padding = 50;
    const gaugeHeight = 30;
    const gaugeSpacing = maxY / 4;
    const y = padding + index * gaugeSpacing + gaugeSpacing / 2 - gaugeHeight / 2;
    const gaugeWidth = canvas.width - padding * 2 - (showLabels ? 180 : 0);
    const x = showLabels ? padding + 160 : padding;

    // Update peak value
    if (value > this.peakValues[index]) {
      this.peakValues[index] = value;
    } else {
      this.peakValues[index] *= this.peakDecay;
    }

    // Draw background track
    ctx.fillStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.beginPath();
    ctx.roundRect(x, y, gaugeWidth, gaugeHeight, 4);
    ctx.fill();

    // Draw filled portion with glow
    if (config.glow) {
      ctx.shadowBlur = 10 + value * 15;
      ctx.shadowColor = accentColor;
    }

    const fillWidth = Math.max(4, value * gaugeWidth);
    ctx.fillStyle = accentColor;
    ctx.beginPath();
    ctx.roundRect(x, y, fillWidth, gaugeHeight, 4);
    ctx.fill();

    ctx.shadowBlur = 0;

    // Draw peak marker
    const peakX = x + this.peakValues[index] * gaugeWidth;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(peakX - 1, y, 2, gaugeHeight);

    // Draw label
    if (showLabels) {
      ctx.fillStyle = '#ffffff';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'right';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, x - 15, y + gaugeHeight / 2);
    }

    // Draw value
    if (showValues) {
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = '12px monospace';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(value.toFixed(3), x + gaugeWidth + 10, y + gaugeHeight / 2);
    }
  }

  /**
   * Render a single frame
   */
  renderFrame(amplitudes, features) {
    if (!features) return;

    const { ctx, canvas, config, featureNames, featureLabels } = this;

    // Get spectral values and apply smoothing
    for (let i = 0; i < 4; i++) {
      const rawValue = features.spectral[featureNames[i]] || 0;
      this.smoothedValues[i] = this.smoothedValues[i] * config.smoothing +
        rawValue * (1 - config.smoothing);
    }

    const padding = 50;
    const availableHeight = canvas.height - padding * 2;

    // Draw title
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 16px sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('Spectral Features', canvas.width / 2, 25);

    // Draw gauges
    for (let i = 0; i < 4; i++) {
      this.drawGauge(featureLabels[i], this.smoothedValues[i], i, availableHeight);
    }

    // Draw scale markers
    ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.font = '10px sans-serif';
    const gaugeX = config.showLabels ? padding + 160 : padding;
    const gaugeWidth = canvas.width - padding * 2 - (config.showLabels ? 180 : 0);

    for (let i = 0; i <= 4; i++) {
      const markerX = gaugeX + (i / 4) * gaugeWidth;
      ctx.textAlign = 'center';
      ctx.fillText((i * 0.25).toFixed(2), markerX, canvas.height - 15);
    }
  }

  /**
   * Clear the canvas and reset state
   */
  clear() {
    super.clear();
    this.smoothedValues.fill(0);
    this.peakValues.fill(0);
  }
}

// Register with the visualizer registry
VisualizerRegistry.register(SpectralAnalyzer);

export { SpectralAnalyzer };
export default SpectralAnalyzer;
